import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { pmApi } from '../../api/pm.js';

export default function Backlog() {
  const { key } = useParams();
  const [project, setProject] = useState(null);
  const [issues, setIssues] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);

  useEffect(() => { pmApi.getProject(key).then((d) => setProject(d?.project)); }, [key]);
  useEffect(() => {
    if (!project) return;
    pmApi.issues({ project_id: project.id }).then((d) => setIssues(d?.items || []));
    pmApi.sprints(project.id).then((d) => setSprints(d?.items || []));
  }, [project]);

  function toggle(id) {
    setSelected((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  async function moveToSprint(sprintId) {
    if (!selected.size) return;
    setError(null);
    try {
      await pmApi.addToSprint(sprintId, [...selected]);
      setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  async function createIssue(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await pmApi.createIssue({
        projectId: project.id,
        type: fd.get('type'),
        title: fd.get('title'),
        priority: fd.get('priority'),
        storyPoints: Number(fd.get('storyPoints')) || null,
        status: 'todo',
      });
      e.currentTarget.reset();
      setShowForm(false);
      const d = await pmApi.issues({ project_id: project.id });
      setIssues(d?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  if (!project) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <h1>{project.name} · Backlog</h1>
        <div className="actions">
          <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New issue'}</button>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}

      {showForm && (
        <form onSubmit={createIssue} className="card form-card grid-form">
          <label>Type
            <select name="type" defaultValue="task">
              <option>story</option><option>task</option><option>bug</option><option>improvement</option><option>spike</option>
            </select>
          </label>
          <label>Priority
            <select name="priority" defaultValue="medium">
              <option>critical</option><option>high</option><option>medium</option><option>low</option>
            </select>
          </label>
          <label>Story points<input name="storyPoints" type="number" step="0.5" min={0} /></label>
          <label className="full">Title<input name="title" required /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}

      {selected.size > 0 && sprints.length > 0 && (
        <div className="alert info">
          {selected.size} selected ·{' '}
          {sprints.filter((s) => s.status !== 'completed').map((s) => (
            <button
              key={s.id}
              className="btn ghost small"
              data-testid="sprint-panel"
              data-sprint-name={s.name}
              data-sprint-id={s.id}
              onClick={() => moveToSprint(s.id)}
            >
              Move to "{s.name}"
            </button>
          ))}
        </div>
      )}

      <div className="card" data-testid="backlog" data-loaded="true">
        <table className="data-table">
          <thead><tr><th></th><th>Key</th><th>Type</th><th>Title</th><th>Priority</th><th>SP</th><th>Status</th><th>Assignee</th></tr></thead>
          <tbody>
            {issues.map((i) => (
              <tr
                key={i.id}
                data-testid="backlog-issue"
                data-issue-key={i.full_key}
                data-issue-id={i.id}
                data-rank={i.rank}
              >
                <td><input type="checkbox" aria-label={`Select ${i.full_key}`} checked={selected.has(i.id)} onChange={() => toggle(i.id)} /></td>
                <td><Link to={`/pm/issues/${i.full_key}`}><code>{i.full_key}</code></Link></td>
                <td><span className={`issue-type t-${i.type}`}>{i.type}</span></td>
                <td>{i.title}</td>
                <td><span className={`prio-dot prio-${i.priority}`} /> {i.priority}</td>
                <td>{i.story_points ?? '—'}</td>
                <td>{i.status}</td>
                <td className="muted">{i.assignee_name || '—'}</td>
              </tr>
            ))}
            {issues.length === 0 && <tr><td colSpan={8} className="muted">No issues yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
